import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from '../router';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Lock, Person, Security as SecurityIcon, AdminPanelSettings, Email, Badge, QrCode2 as QrCodeIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { hardNavigate } from '../utils/postLogoutNavigation';
import axios from 'axios';
import ColorModeToggle from '../components/ColorModeToggle';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const inputSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    color: theme.palette.text.primary,
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 1 },
  },
  '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
});

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  // First-time setup state
  const [needsSetup, setNeedsSetup] = useState(false);
  const [setupUsername, setSetupUsername] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupPasswordConfirm, setSetupPasswordConfirm] = useState('');
  const [setupEmail, setSetupEmail] = useState('');
  const [setupFirstName, setSetupFirstName] = useState('');
  const [setupLastName, setSetupLastName] = useState('');
  const [setupSuccess, setSetupSuccess] = useState(false);

  // MFA login state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const mfaInputRef = useRef(null);

  // Forced MFA setup state (mfa_required but not yet configured)
  const [mfaSetupStep, setMfaSetupStep] = useState(false);
  const [mfaSetupToken, setMfaSetupToken] = useState('');
  const [mfaSetupData, setMfaSetupData] = useState(null); // { qrCode, secret }
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [mfaSetupLoading, setMfaSetupLoading] = useState(false);
  const mfaSetupInputRef = useRef(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { login, verifyMfaLogin, beginMfaSetup, verifyAndCompleteMfaSetup } = useAuth();
  const navigate = useNavigate();

  // Check if first-time setup is needed
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/setup-status`);
        setNeedsSetup(response.data.needsSetup === true);
      } catch (err) {
        if (err.response?.data?.gateway === true) {
          hardNavigate('/');
          return;
        }
        console.error('Failed to check setup status:', err);
      }
      setCheckingSetup(false);
    };
    checkSetupStatus();
  }, []);

  useEffect(() => {
    if (mfaStep && mfaInputRef.current) {
      mfaInputRef.current.focus();
    }
  }, [mfaStep]);

  // Auto-fetch QR code when entering forced MFA setup step
  useEffect(() => {
    if (mfaSetupStep && mfaSetupToken && !mfaSetupData) {
      setMfaSetupLoading(true);
      beginMfaSetup(mfaSetupToken)
        .then((data) => {
          setMfaSetupData(data);
          setTimeout(() => mfaSetupInputRef.current?.focus(), 100);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load QR code');
          setMfaSetupStep(false);
        })
        .finally(() => setMfaSetupLoading(false));
    }
  }, [mfaSetupStep, mfaSetupToken]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login({ username, password });

      if (result.mfaSetupRequired) {
        setMfaSetupToken(result.setupToken);
        setMfaSetupStep(true);
        setLoading(false);
        return;
      }

      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMfaStep(true);
        setLoading(false);
        return;
      }

      if (result.user?.mustChangePassword) {
        navigate('/change-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (setupPassword !== setupPasswordConfirm) {
      setError('Passwords do not match');
      return;
    }

    if (setupPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/setup`, {
        username: setupUsername,
        password: setupPassword,
        email: setupEmail || undefined,
        firstName: setupFirstName || undefined,
        lastName: setupLastName || undefined,
      });
      setSetupSuccess(true);
      setNeedsSetup(false);
      // Pre-fill the login form with the new username
      setUsername(setupUsername);
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mfaCode.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app');
      return;
    }

    setLoading(true);
    try {
      const result = await verifyMfaLogin(mfaToken, mfaCode);
      if (result.user?.mustChangePassword) {
        navigate('/change-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Invalid verification code');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetupSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mfaSetupCode.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app');
      return;
    }

    setLoading(true);
    try {
      const result = await verifyAndCompleteMfaSetup(mfaSetupToken, mfaSetupCode);
      if (result.user?.mustChangePassword) {
        navigate('/change-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Invalid verification code');
      setMfaSetupCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaStep(false);
    setMfaToken('');
    setMfaCode('');
    setMfaSetupStep(false);
    setMfaSetupToken('');
    setMfaSetupData(null);
    setMfaSetupCode('');
    setError('');
    setPassword('');
  };

  if (checkingSetup) {
    return (
      <Box sx={{ minHeight: 'var(--rs-vh)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const brandTileSx = {
    width: 60,
    height: 60,
    borderRadius: '14px',
    background: isDark
      ? 'linear-gradient(145deg, rgba(59,130,246,0.24), rgba(34,211,238,0.10))'
      : 'linear-gradient(145deg, rgba(59,130,246,0.16), rgba(8,145,178,0.08))',
    border: isDark ? '1px solid rgba(96,165,250,0.38)' : '1px solid rgba(59,130,246,0.30)',
    boxShadow: isDark ? '0 16px 36px -14px rgba(59,130,246,0.6)' : '0 16px 36px -18px rgba(59,130,246,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mx: 'auto',
    mb: 2,
  };
  const brandTileIconSx = { fontSize: 30, color: isDark ? '#7dd3fc' : '#0369a1' };

  const successTileSx = {
    ...brandTileSx,
    background: isDark
      ? 'linear-gradient(145deg, rgba(34,197,94,0.22), rgba(34,197,94,0.08))'
      : 'linear-gradient(145deg, rgba(34,197,94,0.16), rgba(34,197,94,0.06))',
    border: isDark ? '1px solid rgba(74,222,128,0.38)' : '1px solid rgba(34,197,94,0.32)',
    boxShadow: isDark ? '0 16px 36px -14px rgba(34,197,94,0.5)' : '0 16px 36px -18px rgba(34,197,94,0.4)',
  };
  const successTileIconSx = { fontSize: 30, color: isDark ? '#4ade80' : '#16a34a' };

  return (
    <Box
      sx={{
        minHeight: 'var(--rs-vh)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'transparent',
        position: 'relative',
        p: 3,
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <ColorModeToggle placement="bottom" />
      </Box>
      <Card
        sx={{
          maxWidth: needsSetup || mfaSetupStep ? 480 : 400,
          width: '100%',
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (muiTheme) => muiTheme.palette.mode === 'dark'
            ? '0 0 0 1px rgba(255,255,255,0.05), 0 24px 64px -24px rgba(0,0,0,0.8), 0 0 90px -22px rgba(34,211,238,0.28)'
            : '0 24px 64px -32px rgba(15,23,42,0.22)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {needsSetup ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box sx={successTileSx}>
                  <AdminPanelSettings sx={successTileIconSx} />
                </Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  RadioSuiteX
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Welcome! Create your Super Admin account to get started.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSetupSubmit}>
                <TextField
                  fullWidth
                  label="Username"
                  value={setupUsername}
                  onChange={(e) => setSetupUsername(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="username"
                  InputProps={{
                    startAdornment: <Person sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={inputSx}
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={setupFirstName}
                    onChange={(e) => setSetupFirstName(e.target.value)}
                    margin="normal"
                    InputProps={{
                      startAdornment: <Badge sx={{ color: 'text.secondary', mr: 1 }} />,
                    }}
                    sx={inputSx}
                  />
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={setupLastName}
                    onChange={(e) => setSetupLastName(e.target.value)}
                    margin="normal"
                    sx={inputSx}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Email (optional)"
                  type="email"
                  value={setupEmail}
                  onChange={(e) => setSetupEmail(e.target.value)}
                  margin="normal"
                  autoComplete="email"
                  InputProps={{
                    startAdornment: <Email sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={setupPassword}
                  onChange={(e) => setSetupPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="new-password"
                  helperText="Minimum 8 characters"
                  InputProps={{
                    startAdornment: <Lock sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={setupPasswordConfirm}
                  onChange={(e) => setSetupPasswordConfirm(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: <Lock sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={inputSx}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="success"
                  disabled={loading}
                  sx={{ mt: 3, mb: 1, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Super Admin Account'}
                </Button>
              </form>
            </>
          ) : mfaSetupStep ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box sx={successTileSx}>
                  <QrCodeIcon sx={successTileIconSx} />
                </Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  Set Up Two-Factor Authentication
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your account requires MFA. Scan the QR code below with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.), then enter the 6-digit code to continue.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {mfaSetupLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress color="success" />
                </Box>
              ) : mfaSetupData ? (
                <form onSubmit={handleMfaSetupSubmit}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Box
                      component="img"
                      src={mfaSetupData.qrCode}
                      alt="MFA QR Code"
                      sx={{
                        width: 220,
                        height: 220,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: '#ffffff',
                        p: 1,
                      }}
                    />
                  </Box>

                  <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                    <strong>Can't scan?</strong> Enter this key manually in your app:
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        mt: 0.5,
                        wordBreak: 'break-all',
                        color: isDark ? '#67e8f9' : '#0891b2',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                      }}
                    >
                      {mfaSetupData.secret}
                    </Box>
                  </Alert>

                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={mfaSetupCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setMfaSetupCode(val);
                    }}
                    margin="normal"
                    required
                    autoComplete="one-time-code"
                    inputRef={mfaSetupInputRef}
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      style: {
                        textAlign: 'center',
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        letterSpacing: '0.5em',
                        fontFamily: 'JetBrains Mono, monospace',
                      },
                    }}
                    InputProps={{
                      startAdornment: <SecurityIcon sx={{ color: 'success.main', mr: 1 }} />,
                    }}
                    sx={(muiTheme) => ({
                      ...inputSx(muiTheme),
                      '& .MuiInputLabel-root.Mui-focused': { color: muiTheme.palette.success.main },
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: muiTheme.palette.success.main },
                        '&.Mui-focused fieldset': { borderColor: muiTheme.palette.success.main },
                      },
                    })}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="success"
                    disabled={loading || mfaSetupCode.length !== 6}
                    sx={{ mt: 3, mb: 1, py: 1.5 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Sign In'}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={handleBackToLogin}
                    color="inherit"
                    sx={{ color: 'text.secondary' }}
                  >
                    Back to Login
                  </Button>
                </form>
              ) : null}
            </>
          ) : !mfaStep ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={brandTileSx}>
                  <Lock sx={brandTileIconSx} />
                </Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  RadioSuiteX
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sign in to your account
                </Typography>
              </Box>

              {setupSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Account created successfully! Sign in to continue.
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="username"
                  InputProps={{
                    startAdornment: <Person sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: <Lock sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={inputSx}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box sx={successTileSx}>
                  <SecurityIcon sx={successTileIconSx} />
                </Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  Two-Factor Authentication
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter the 6-digit code from your authenticator app
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleMfaSubmit}>
                <TextField
                  fullWidth
                  label="Verification Code"
                  value={mfaCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setMfaCode(val);
                  }}
                  margin="normal"
                  required
                  autoComplete="one-time-code"
                  inputRef={mfaInputRef}
                  placeholder="000000"
                  inputProps={{
                    maxLength: 6,
                    style: {
                      textAlign: 'center',
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      letterSpacing: '0.5em',
                      fontFamily: 'JetBrains Mono, monospace',
                    },
                  }}
                  InputProps={{
                    startAdornment: <SecurityIcon sx={{ color: 'success.main', mr: 1 }} />,
                  }}
                  sx={(muiTheme) => ({
                    ...inputSx(muiTheme),
                    '& .MuiInputLabel-root.Mui-focused': { color: muiTheme.palette.success.main },
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: muiTheme.palette.success.main },
                      '&.Mui-focused fieldset': { borderColor: muiTheme.palette.success.main },
                    },
                  })}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="success"
                  disabled={loading || mfaCode.length !== 6}
                  sx={{ mt: 3, mb: 1, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={handleBackToLogin}
                  color="inherit"
                  sx={{ color: 'text.secondary' }}
                >
                  Back to Login
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
